'use client';

import { BulkUrlForm } from '@/components/bulk-url-form';
import { UrlForm } from '@/components/url-form';
import { UrlResult } from '@/components/url-result';
import { URLRotatorForm } from '@/components/url-rotator-form';
import { useLocale } from '@/lib/i18n/use-locale';
import { getCsrfHeaders } from '@/lib/utils/csrf';
import { useEffect, useState } from 'react';

type HomeClientProps = {
  initialUser?: { id: string } | null;
};

export function HomeClient({ initialUser = null }: HomeClientProps) {
  const [result, setResult] = useState<{
    shortUrl: string;
    shortCode: string;
    originalUrl: string;
    expiresAt: string | null;
  } | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(!!initialUser);
  const [checkingAuth, setCheckingAuth] = useState(false);
  const [activeTab, setActiveTab] = useState<'single' | 'bulk' | 'rotator'>('single');

  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'bulk' || hash === 'rotator' || hash === 'single') {
        setActiveTab(hash);
      }
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  const setTabWithHash = (tab: 'single' | 'bulk' | 'rotator') => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };
  const { t } = useLocale();
  const sponsorLogos = [
    { key: 'sponsorLogoOne', src: '/sponsors/disney.svg' },
    { key: 'sponsorLogoTwo', src: '/sponsors/alibaba.svg' },
    { key: 'sponsorLogoSix', src: '/sponsors/digitalocean.svg' },
    { key: 'sponsorLogoEleven', src: '/sponsors/stripe.svg' },
    { key: 'sponsorLogoThree', src: '/sponsors/booking.svg' },
    { key: 'sponsorLogoNine', src: '/sponsors/salesforce.svg' },
    { key: 'sponsorLogoFour', src: '/sponsors/redhat.svg' },
    { key: 'sponsorLogoFive', src: '/sponsors/ibm.svg' },

    { key: 'sponsorLogoSeven', src: '/sponsors/netlify.svg' },
    { key: 'sponsorLogoEight', src: '/sponsors/vercel.svg' },

    { key: 'sponsorLogoTen', src: '/sponsors/shopify.svg' },
  ] as const;

  const openBulkTab = async () => {
    setCheckingAuth(true);
    try {
      const response = await fetch('/api/auth/me');
      const data = await response.json();
      if (data.user) {
        setIsAuthenticated(true);
        setActiveTab('bulk');
        return;
      }
      window.location.href = '/login';
    } catch {
      window.location.href = '/login';
    } finally {
      setCheckingAuth(false);
    }
  };

  const openRotatorTab = async () => {
    setCheckingAuth(true);
    try {
      const response = await fetch('/api/auth/me');
      const data = await response.json();
      if (data.user) {
        setIsAuthenticated(true);
        setActiveTab('rotator');
        return;
      }
      window.location.href = '/login';
    } catch {
      window.location.href = '/login';
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleShorten = async (data: {
    url: string;
    customCode?: string;
    expiresIn?: number;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
  }) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const csrfHeaders = await getCsrfHeaders();
      const response = await fetch('/api/shorten', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...csrfHeaders,
        },
        body: JSON.stringify(data),
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error || 'Failed to shorten URL');
      }

      setResult(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className='relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-sky-100 dark:from-gray-950 dark:via-gray-900 dark:to-slate-900'>
      <div className='pointer-events-none absolute -top-32 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl' />
      <div className='pointer-events-none absolute right-[-120px] top-20 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl' />

      <section className='relative mx-auto flex max-w-6xl flex-col gap-10 px-4 pb-16 pt-8 lg:pb-24 lg:pt-12'>
        <div className='flex flex-col gap-2 lg:flex-row lg:items-center'>
          <div className='flex-1'>
            <div className='flex flex-wrap items-center gap-3'>
              <div className='inline-flex items-center gap-2 rounded-full border border-blue-200/60 bg-white/80 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-blue-600 shadow-sm dark:border-blue-400/30 dark:bg-gray-900/60 dark:text-blue-300'>
                {t('home.tagline')}
              </div>
            </div>

            <h1 className='mt-6 text-2xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-3xl'>
              {t('home.heroTitle')}
            </h1>
            <p className='mt-4 max-w-xl text-lg leading-relaxed text-gray-600 dark:text-gray-300'>
              {t('home.featureAnalyticsBody')}
            </p>
            <div className='mt-3 inline-flex items-center gap-1 rounded-2xl border border-blue-100/70 bg-white/80 px-4 py-3 text-xs font-semibold uppercase tracking-widest text-blue-700 shadow-sm dark:border-blue-900/40 dark:bg-gray-900/70 dark:text-blue-200'>
              <span className='inline-flex items-center gap-1 font-mono text-[11px] tracking-normal text-gray-700 dark:text-gray-200'>
                byt.la/
                <span className='typing-text text-blue-700 dark:text-blue-300'>yourbrand </span>
                <span className='typing-caret'>|</span>
              </span>
            </div>
            <style jsx>{`
              .typing-text {
                display: inline-block;
                overflow: hidden;
                white-space: nowrap;
                animation: typing 3s steps(10, end) infinite alternate;
              }
              @keyframes float {
                0%,
                100% {
                  transform: translateY(0);
                }
                50% {
                  transform: translateY(-4px);
                }
              }
              .typing-caret {
                margin-left: 2px;
                animation: blink 1s step-end infinite;
              }
              @keyframes typing {
                from {
                  width: 0;
                }
                to {
                  width: 10ch;
                }
              }
              @keyframes blink {
                50% {
                  opacity: 0;
                }
              }
            `}</style>
            <div className='mt-8 flex flex-wrap items-center gap-4'>
              {!isAuthenticated && (
                <>
                  <a
                    href='/register'
                    className='hidden md:inline-block rounded-full bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition hover:-translate-y-0.5 hover:bg-blue-700'
                  >
                    {t('home.signUpCta')}
                  </a>
                  <a
                    href='/login'
                    className='hidden md:inline-block rounded-full border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 transition hover:border-gray-400 hover:text-gray-900 dark:border-gray-700 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:text-white'
                  >
                    {t('home.signInCta')}
                  </a>
                </>
              )}
            </div>
          </div>

          <div className='relative flex-1'>
            <div className='rounded-2xl border border-white/60 bg-white/80 p-4 shadow-2xl shadow-blue-500/10 backdrop-blur dark:border-white/10 dark:bg-gray-900/70'>
              <div className='mb-6 grid grid-cols-3 gap-2 border-b border-gray-200/70 pb-4 text-xs font-semibold uppercase tracking-widest text-gray-400 dark:border-gray-700'>
                <button
                  type='button'
                onClick={() => setTabWithHash('single')}
                className={`relative flex h-10 w-full items-center justify-center gap-2 rounded-md px-2 text-[11px] uppercase tracking-widest transition sm:text-xs ${
                    activeTab === 'single'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-900 hover:bg-slate-300 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600'
                  }`}
                >
                  <svg
                    className='h-4 w-4'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'
                  >
                    <path d='M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1' />
                    <path d='M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1' />
                  </svg>
                  <span className='text-[10px] sm:text-xs'>{t('home.tabSingle')}</span>
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setTabWithHash('bulk');
                    openBulkTab();
                  }}
                  className={`relative flex h-10 w-full items-center justify-center gap-2 rounded-md px-2 text-[11px] uppercase tracking-widest transition sm:text-xs ${
                    activeTab === 'bulk'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-900 hover:bg-slate-300 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600'
                  }`}
                >
                  <svg
                    className='h-4 w-4'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'
                  >
                    <rect x='3' y='4' width='14' height='14' rx='2' />
                    <path d='M7 8h6M7 12h6M7 16h4' />
                    <path d='M17 7h4M17 11h4M17 15h4' />
                  </svg>
                  <span className='text-[10px] sm:text-xs'>
                    {checkingAuth ? t('home.signIn') : t('home.tabBulk')}
                  </span>
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setTabWithHash('rotator');
                    openRotatorTab();
                  }}
                  className={`relative flex h-10 w-full items-center justify-center gap-2 rounded-md px-2 text-[11px] uppercase tracking-widest transition sm:text-xs ${
                    activeTab === 'rotator'
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-900 hover:bg-slate-300 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600'
                  }`}
                >
                  {checkingAuth ? (
                    <>
                      <svg
                        className='h-4 w-4'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        aria-hidden='true'
                      >
                        <path d='M9 18h6' />
                        <path d='M10 22h4' />
                        <path d='M8 14a6 6 0 1 1 8 0c-1.2 1-2 2.4-2.3 4H10.3c-.3-1.6-1.1-3-2.3-4z' />
                      </svg>
                      <span className='text-[10px] sm:text-xs'>{t('home.signIn')}</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className='h-4 w-4'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        aria-hidden='true'
                      >
                        <path d='M9 18h6' />
                        <path d='M10 22h4' />
                        <path d='M8 14a6 6 0 1 1 8 0c-1.2 1-2 2.4-2.3 4H10.3c-.3-1.6-1.1-3-2.3-4z' />
                      </svg>
                      <span className='text-[10px] sm:text-xs'>{t('home.tabRotator')}</span>
                      <span className='absolute -top-3 right-2 inline-flex items-center gap-1 rounded-full bg-amber-700 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white shadow-[0_0_14px_rgba(245,158,11,0.55)] dark:bg-amber-300 dark:text-amber-950 animate-[float_2.2s_ease-in-out_infinite]'>
                        <svg
                          className='h-3 w-3'
                          viewBox='0 0 24 24'
                          fill='currentColor'
                          aria-hidden='true'
                        >
                          <path d='M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z' />
                        </svg>
                        {t('home.tabRotatorBadge')}
                      </span>
                    </>
                  )}
                </button>
              </div>

              {activeTab === 'single' && (
                <>
                  <UrlForm onSubmit={handleShorten} loading={loading} />
                  {error && (
                    <div className='mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/30 dark:text-red-200'>
                      {error}
                    </div>
                  )}
                  {result && (
                    <div className='mt-6'>
                      <UrlResult result={result} />
                    </div>
                  )}
                </>
              )}

              {activeTab === 'bulk' && (
                <>
                  {isAuthenticated ? (
                    <div className='space-y-4'>
                      <div className='rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700 dark:border-blue-900/60 dark:bg-blue-900/30 dark:text-blue-200'>
                        <p className='mb-1 font-semibold'>{t('bulk.title')}</p>
                        <p className='text-xs'>{t('bulk.subtitle')}</p>
                      </div>
                      <BulkUrlForm />
                    </div>
                  ) : (
                    <div className='rounded-2xl border border-blue-200 bg-blue-50 p-6 text-sm text-blue-700 dark:border-blue-900/60 dark:bg-blue-900/30 dark:text-blue-200'>
                      {t('home.bulkSignIn')}
                      <a
                        href='/login'
                        className='ml-2 font-semibold text-blue-700 underline underline-offset-4 hover:text-blue-900 dark:text-blue-200'
                      >
                        {t('home.signIn')}
                      </a>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'rotator' && (
                <>
                  {isAuthenticated ? (
                    <div className='space-y-4'>
                      <div className='rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700 dark:border-blue-900/60 dark:bg-blue-900/30 dark:text-blue-200'>
                        <p className='mb-1 font-semibold'>{t('rotator.title')}</p>
                        <p className='text-xs'>{t('rotator.description')}</p>
                      </div>
                      <URLRotatorForm />
                      <div className='flex justify-end'>
                        <a
                          href='/rotations'
                          className='text-xs font-semibold uppercase tracking-widest text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        >
                          {t('rotator.manageTitle')}
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className='rounded-2xl border border-blue-200 bg-blue-50 p-6 text-sm text-blue-700 dark:border-blue-900/60 dark:bg-blue-900/30 dark:text-blue-200'>
                      {t('home.rotatorSignIn')}
                      <a
                        href='/login'
                        className='ml-2 font-semibold text-blue-700 underline underline-offset-4 hover:text-blue-900 dark:text-blue-200'
                      >
                        {t('home.signIn')}
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className='mx-auto max-w-6xl px-4 pb-16'>
        <div className='rounded-3xl border border-blue-100/70 bg-white/90 px-6 py-10 shadow-lg shadow-blue-100/40 dark:border-blue-900/40 dark:bg-gray-900/70'>
          <div className='max-w-3xl space-y-5 text-base leading-relaxed text-gray-600 dark:text-gray-300'>
            <p>{t('home.longDescription1')}</p>
            <p>{t('home.longDescription2')}</p>
            <p>{t('home.longDescription3')}</p>
            <div className='flex flex-wrap gap-3 pt-2 text-sm font-semibold'>
              <a
                href='/about'
                className='rounded-full border border-blue-200/70 bg-blue-50 px-4 py-2 text-blue-700 transition hover:border-blue-300 hover:bg-blue-100 dark:border-blue-900/50 dark:bg-blue-900/30 dark:text-blue-200'
              >
                {t('home.learnAboutLink')}
              </a>
              <a
                href='/faq'
                className='rounded-full border border-gray-200 px-4 py-2 text-gray-700 transition hover:border-gray-300 hover:text-gray-900 dark:border-gray-700 dark:text-gray-200 dark:hover:border-gray-500'
              >
                {t('home.readFaqLink')}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className='mx-auto max-w-6xl px-4 pb-20'>
        <div className='mb-10 flex items-center justify-between'>
          <h2 className='text-2xl font-semibold text-gray-900 dark:text-white'>
            {t('home.featuresTitle')}
          </h2>
        </div>
        <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-4'>
          <div className='rounded-2xl border border-gray-200/70 bg-white p-6 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none'>
            <h3 className='text-base font-semibold text-gray-900 dark:text-white'>
              {t('home.featureCustomTitle')}
            </h3>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-300'>
              {t('home.featureCustomBody')}
            </p>
          </div>
          <div className='rounded-2xl border border-gray-200/70 bg-white p-6 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none'>
            <h3 className='text-base font-semibold text-gray-900 dark:text-white'>
              {t('home.featureAnalyticsTitle')}
            </h3>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-300'>
              {t('home.featureAnalyticsBody')}
            </p>
          </div>
          <div className='rounded-2xl border border-gray-200/70 bg-white p-6 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none'>
            <h3 className='text-base font-semibold text-gray-900 dark:text-white'>
              {t('home.featureExpirationTitle')}
            </h3>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-300'>
              {t('home.featureExpirationBody')}
            </p>
          </div>
          <div className='rounded-2xl border border-gray-200/70 bg-white p-6 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none'>
            <h3 className='text-base font-semibold text-gray-900 dark:text-white'>
              {t('home.featureBulkTitle')}
            </h3>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-300'>
              {t('home.featureBulkBody')}
            </p>
          </div>
          <div className='rounded-2xl border border-gray-200/70 bg-white p-6 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none'>
            <h3 className='text-base font-semibold text-gray-900 dark:text-white'>
              {t('home.featureUtmTitle')}
            </h3>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-300'>
              {t('home.featureUtmBody')}
            </p>
          </div>
          <div className='rounded-2xl border border-gray-200/70 bg-white p-6 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none'>
            <h3 className='text-base font-semibold text-gray-900 dark:text-white'>
              {t('home.featureQrTitle')}
            </h3>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-300'>
              {t('home.featureQrBody')}
            </p>
          </div>
          <div className='rounded-2xl border border-gray-200/70 bg-white p-6 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none'>
            <h3 className='text-base font-semibold text-gray-900 dark:text-white'>
              {t('home.featureFreeTitle')}
            </h3>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-300'>
              {t('home.featureFreeBody')}
            </p>
          </div>
          <div className='rounded-2xl border border-gray-200/70 bg-white p-6 shadow-lg shadow-gray-100/60 dark:border-gray-800 dark:bg-gray-900 dark:shadow-none'>
            <h3 className='text-base font-semibold text-gray-900 dark:text-white'>
              {t('home.featureNoCardTitle')}
            </h3>
            <p className='mt-2 text-sm text-gray-600 dark:text-gray-300'>
              {t('home.featureNoCardBody')}
            </p>
          </div>
        </div>
      </section>

      <section className='mx-auto max-w-6xl px-4 pb-24'>
        <div className='rounded-3xl border border-gray-200/70 bg-white/80 px-6 py-10 shadow-lg shadow-gray-100/60 backdrop-blur dark:border-gray-800 dark:bg-gray-900/70 dark:shadow-none'>
          <div className='flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'>
            <div>
              <p className='text-xs font-semibold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-300'>
                {t('home.sponsorTitle')}
              </p>
              <h2 className='mt-3 text-2xl font-semibold text-gray-900 dark:text-white'>
                {t('home.sponsorSubtitle')}
              </h2>
            </div>
            <div className='flex flex-wrap items-center gap-4'>
              {sponsorLogos.map(logo => (
                <div
                  key={logo.key}
                  className='flex items-center justify-center rounded-xl bg-white/90 px-3 py-2 shadow-sm transition hover:-translate-y-0.5 dark:bg-white'
                >
                  <img
                    src={logo.src}
                    alt={t(`home.${logo.key}`)}
                    className='h-6 w-auto max-w-[80px] md:h-8 md:max-w-[140px]'
                    loading='lazy'
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
